import { inject, observer } from 'mobx-react';
import { Link } from 'react-router-dom';
import * as React from 'react';

import { ClickTracker } from 'bernie-analytics';
import { LocalizedText } from 'bernie-l10n';
import { QueryRoute, RouteStore } from 'bernie-core';

import { CurrentHotelStore } from 'stores/current-hotel-store';

import { UitkLink } from 'uitk-react-link';

import { FocusTarget } from 'components/common/focus/focus-target';
import { ReviewCountBars } from './review-count-bars';
import { ReviewsDialogLayer } from './reviews-dialog-layer';

export interface ReviewsSummaryProps {
  currentHotel?: CurrentHotelStore;
  routeStore?: RouteStore;
}

export interface ReviewsSummaryState {
  initialized: boolean;
}

const LinkTracked = ClickTracker(Link);

@inject('currentHotel', 'routeStore')
@observer
export class ReviewsSummary extends React.Component<ReviewsSummaryProps, ReviewsSummaryState> {
  public constructor(props) {
    super(props);
    this.state = {
      initialized: false
    };
    this.onReviewsDialogClosed = this.onReviewsDialogClosed.bind(this);
  }

  public componentDidMount() {
    this.setState({ initialized: true });
  }

  private onReviewsDialogClosed() {
    const { routeStore } = this.props;
    routeStore.goBack();
  }

  public render() {
    const { currentHotel, routeStore } = this.props;
    const { searchCriteria } = currentHotel;
    const { initialized } = this.state;
    const reviewButtonClassname = !initialized ? 'is-disabled' : null;
    const { reviews } = currentHotel;

    const {
      localizedOverallRating,
      superlative,
      localizedCountWithLabel,
      localizedCleanliness,
      cleanlinessPercent,
      localizedServiceAndStaff,
      serviceAndStaffPercent,
      localizedAmenityScore,
      amenityScorePercent,
      localizedHotelCondition,
      hotelConditionPercent
    } = reviews;

    if (reviews.userReviews && reviews.userReviews.length > 0) {
      return (
        <>
          <QueryRoute query={{ pwaDialog: 'reviews-on-infosite-dialog' }}>
            <ReviewsDialogLayer searchCriteria={searchCriteria} onReviewsDialogClosed={this.onReviewsDialogClosed} />
          </QueryRoute>
          <FocusTarget targetKey="reviews">
            <div className="uitk-grid all-b-padding-six">
              <div className="uitk-flex">
                <div className="uitk-type-900">{localizedOverallRating}</div>
                <div className="uitk-type-500 all-l-padding-three">
                  <p className="uitk-cell uitk-type-bold all-t-padding-one">
                    {superlative ? superlative : <LocalizedText message="hotels.infosite.reviews.superlative.guestRating" />}
                  </p>
                  <div className={`uitk-type-300 ${reviewButtonClassname}`}>
                    <ReviewCountButton localizedCountWithLabel={localizedCountWithLabel} routeStore={routeStore} />
                  </div>
                </div>
              </div>
              <div className="uitk-grid all-t-padding-three">
                <ReviewCountBars />
              </div>
              <div className="uitk-grid all-t-padding-six all-y-gutter-six">
                {cleanlinessPercent > 0 && (
                  <div className="uitk-cell all-cell-1-2" data-stid="cleanliness-score">
                    <ReviewSummaryScore score={localizedCleanliness} label="hotels.infosite.reviews.roomCleanliness.title" />
                  </div>
                )}
                {serviceAndStaffPercent > 0 && (
                  <div className="uitk-cell all-cell-1-2" data-stid="service-and-staff-score">
                    <ReviewSummaryScore score={localizedServiceAndStaff} label="hotels.infosite.reviews.serviceAndStaff.title" />
                  </div>
                )}
                {amenityScorePercent > 0 && (
                  <div className="uitk-cell all-cell-1-2" data-stid="amenity-score">
                    <ReviewSummaryScore score={localizedAmenityScore} label="hotels.infosite.reviews.amenities.title" />
                  </div>
                )}
                {hotelConditionPercent > 0 && (
                  <div className="uitk-cell all-cell-1-2" data-stid="hotel-condition-score">
                    <ReviewSummaryScore score={localizedHotelCondition} label="hotels.infosite.reviews.hotelCondition.title" />
                  </div>
                )}
              </div>
            </div>
          </FocusTarget>
        </>
      );
    }

    return null;
  }
}

interface ReviewSummaryScoreModel {
  score: string;
  label: string;
}
const ReviewSummaryScore = ({ score, label }: ReviewSummaryScoreModel) => (
  <>
    <div className="uitk-type-500 uitk-type-bold">{score}</div>
    <div>
      <LocalizedText message={label} />
    </div>
  </>
);
ReviewSummaryScore['displayName'] = 'ReviewSummaryScore';

interface ReviewCountButtonModel {
  localizedCountWithLabel: string;
  routeStore: RouteStore;
}
const ReviewCountButton = observer(({ localizedCountWithLabel, routeStore }: ReviewCountButtonModel) => (
  <UitkLink>
    <LinkTracked
      linkName="Display detailed reviews"
      rfrr="UGC.ReviewCountResults"
      events="event419=10"
      to={{
        pathname: routeStore.pathname,
        search: routeStore.mergeQueryString(routeStore.search, { pwaDialog: 'reviews-on-infosite-dialog' })
      }}
    >
      {localizedCountWithLabel}
    </LinkTracked>
  </UitkLink>
));
ReviewCountButton['displayName'] = 'ReviewCountButton';
